import {AnalyticsEventMetadata} from 'analytics/AnalyticsTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import * as Context from 'Context';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeStudiesCreateStudyEventMetadata,
  STUDIES_CREATE_STUDY_ENDED_EVENT,
  STUDIES_CREATE_STUDY_STARTED_EVENT,
  ExecutionType,
} from 'performance/processStudiesEvents';
import {OnUnknownErrorAction} from 'studies/sidepanel/CreationTrayTask';
import {showStudyCreationTrayTask} from 'studies/sidepanel/StudyCreationTrayTask';
import {getTypedStudyAdapter} from 'studies/typed/TypedStudyAdapters';
import {TypedStudyPerformanceMetadata} from 'studies/typed/utils/TypedStudyTypes';
import {withLoadingScreen} from 'util/Promises';

import {createStudies, CreateStudiesResponse, CreateStudyParams} from '../StudyDAO';

export async function onCreateStudy(
  creates: Array<CreateStudyParams>,
  actionOrigin: ActionOrigin,
  studyType: StudyType,
  analyticsMetadata: AnalyticsEventMetadata,
  typeSpecificPerformanceMetadata: TypedStudyPerformanceMetadata,
  callbacks: {
    onSuccess: (async: boolean) => void;
    onOpen: (response: CreateStudiesResponse) => void;
    onBackground: (isBackground: boolean) => void;
    onUnknownError: OnUnknownErrorAction;
  }
): Promise<void> {
  const adapter = getTypedStudyAdapter(studyType);
  await Context.usingAsync(
    {
      analyticsMetadata: {
        actionOrigin,
        studyType,
        studyTypeMetadata: analyticsMetadata,
      },
    },
    null,
    async () => {
      logProfilerTimingEvent(STUDIES_CREATE_STUDY_STARTED_EVENT);
      const initResponse = await withLoadingScreen(
        createStudies(creates, {supportsAsync: adapter.creation.async}),
        'Creating study...'
      );
      if (!initResponse.async) {
        callbacks.onSuccess(false);
        callbacks.onOpen(initResponse.data);
        // Users can navigate away from the page before the study creation completes,
        // In which case we will miss this event. This is a best effort to log the event.
        logProfilerTimingEvent(
          STUDIES_CREATE_STUDY_ENDED_EVENT,
          composeStudiesCreateStudyEventMetadata(
            studyType,
            ExecutionType.SYNC,
            actionOrigin,
            typeSpecificPerformanceMetadata
          )
        );
        return;
      }
      callbacks.onBackground(true);
      showStudyCreationTrayTask(
        initResponse.data.taskId,
        initResponse.data.status_url,
        initResponse.taskMeta,
        creates,
        {
          onSuccess: () => {
            callbacks.onSuccess(true);
            // Users can navigate away from the page before the study creation completes,
            // In which case we will miss this event. This is a best effort to log the event.
            logProfilerTimingEvent(
              STUDIES_CREATE_STUDY_ENDED_EVENT,
              composeStudiesCreateStudyEventMetadata(
                studyType,
                ExecutionType.ASYNC,
                actionOrigin,
                typeSpecificPerformanceMetadata
              )
            );
          },
          onOpen: callbacks.onOpen,
          onUnknownError: callbacks.onUnknownError,
          onRetry: () => {
            callbacks.onBackground(false);
          },
        },
        initResponse.data.timingMeta
      );
    }
  );
}
